/**
 * LocalStorage-Persistierung mit v1->v2-Migration.
 *
 * KRITISCH: Storage-Key 'shk-webtrainer-v2' bleibt identisch zum alten Webtrainer.
 * Andys FSRS-State darf NICHT verloren gehen.
 *
 * Schema v2 (gleiches Format wie alter Webtrainer):
 *   {
 *     version: 2,
 *     cards: { [cardId: string]: SerializedFsrsState },
 *     filter: PersistedFilterState,
 *     reviewLog: ReviewLogEntry[]   // optional, für FSRS-Optimizer-Export
 *   }
 *
 * Tests in tests/store-migration.test.ts garantieren Roundtrip.
 */

import type { State } from 'ts-fsrs';
import { type FsrsCard, newCard } from '$lib/fsrs';

export const STORAGE_KEY = 'shk-webtrainer-v2';

export interface SerializedFsrsState {
  due: string;
  stability: number;
  difficulty: number;
  elapsed_days: number;
  scheduled_days: number;
  reps: number;
  lapses: number;
  state: State;
  last_review?: string;
}

export interface ReviewLogEntry {
  cardId: string;
  rating: 1 | 2 | 3 | 4;
  reviewedAt: string;
  dueAfter: string;
  stability: number;
  difficulty: number;
}

export interface PersistedFilterState {
  burningTopics: string[];
  decks: string[];
  searchQuery: string;
  cardTypes: string[];
  sources: string[];
  tags: string[];
  mode: 'review' | 'study' | 'all';
}

export interface PersistedState {
  version: 2;
  cards: Record<string, SerializedFsrsState>;
  filter: PersistedFilterState;
  reviewLog: ReviewLogEntry[];
}

const EMPTY_STATE: PersistedState = {
  version: 2,
  cards: {},
  filter: {
    burningTopics: [],
    decks: [],
    searchQuery: '',
    cardTypes: [],
    sources: [],
    tags: [],
    mode: 'review',
  },
  reviewLog: [],
};

export function serialize(card: FsrsCard): SerializedFsrsState {
  return {
    due: card.due.toISOString(),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state,
    last_review: card.last_review?.toISOString(),
  };
}

export function deserialize(s: SerializedFsrsState): FsrsCard {
  const base = newCard();
  return {
    ...base,
    due: new Date(s.due),
    stability: s.stability,
    difficulty: s.difficulty,
    elapsed_days: s.elapsed_days,
    scheduled_days: s.scheduled_days,
    reps: s.reps,
    lapses: s.lapses,
    state: s.state,
    last_review: s.last_review ? new Date(s.last_review) : undefined,
  };
}

/**
 * Migriert von v1 (altes Webtrainer-Format vor Mai 2026) auf v2.
 * v1-Schema war flach: { [cardId]: { due, stability, ... } }.
 */
export function migrate(raw: unknown): PersistedState {
  if (!raw || typeof raw !== 'object') return structuredClone(EMPTY_STATE);
  const obj = raw as Record<string, unknown>;
  if (obj.version === 2 && typeof obj.cards === 'object') {
    // structuredClone: niemals Referenzen ins frisch geparste JSON zurückgeben,
    // sonst mutieren spätere Store-Updates versehentlich das Quell-Objekt.
    return {
      version: 2,
      cards: structuredClone((obj.cards ?? {}) as Record<string, SerializedFsrsState>),
      filter: {
        ...EMPTY_STATE.filter,
        ...structuredClone((obj.filter ?? {}) as Partial<PersistedFilterState>),
      },
      reviewLog: Array.isArray(obj.reviewLog)
        ? structuredClone(obj.reviewLog as ReviewLogEntry[])
        : [],
    };
  }
  // v1 -> v2: alle Top-Level-Keys sind Card-IDs
  const cards: Record<string, SerializedFsrsState> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k === 'version' || k === 'filter' || k === 'reviewLog') continue;
    if (v && typeof v === 'object' && 'due' in (v as Record<string, unknown>)) {
      cards[k] = v as SerializedFsrsState;
    }
  }
  return { ...structuredClone(EMPTY_STATE), cards };
}

export function load(storage: Storage = globalThis.localStorage): PersistedState {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return structuredClone(EMPTY_STATE);
    return migrate(JSON.parse(raw));
  } catch {
    return structuredClone(EMPTY_STATE);
  }
}

/**
 * Obergrenze für reviewLog-Einträge im LocalStorage. Der Log wächst sonst
 * unbegrenzt (täglich neue Reviews) und sprengt früher oder später die
 * ~5-MB-Quota — wodurch JEDER weitere save() fehlschlägt und FSRS-Historie
 * verloren ginge. Bei Quota-Überlauf trimmen wir auf die jüngsten N Einträge.
 *
 * 20000 statt 2000: fsrs_optimize.py braucht die volle Historie bis Dez 2026
 * (~8 Monate × ~50 Reviews/Tag ≈ 12k); ein Log-Eintrag ist ~160 B → ~3 MB
 * worst case, unter der 5-MB-Quota. Trim greift ohnehin nur bei QuotaExceeded.
 */
export const MAX_REVIEW_LOG = 20000;

function isQuotaError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { name?: string }).name === 'QuotaExceededError'
  );
}

export function save(state: PersistedState, storage: Storage = globalThis.localStorage): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    if (!isQuotaError(err)) throw err;
    // Quota voll: reviewLog auf jüngste MAX_REVIEW_LOG Einträge kürzen + Retry.
    const trimmed: PersistedState = { ...state, reviewLog: state.reviewLog.slice(-MAX_REVIEW_LOG) };
    storage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  }
}

export function emptyState(): PersistedState {
  return structuredClone(EMPTY_STATE);
}

/** Standard-Filter (alle Facetten leer, mode='review') — auch der Reset-Zustand. */
export function defaultFilter(): PersistedFilterState {
  return structuredClone(EMPTY_STATE.filter);
}
